import CaretDown from '@mds/icons/CaretDown';
import CaretLeftFill from '@mds/icons/CaretLeftFill';
import CaretLineLeftFill from '@mds/icons/CaretLineLeftFill';
import CaretLineRightFill from '@mds/icons/CaretLineRightFill';
import CaretRightFill from '@mds/icons/CaretRightFill';
import CheckCircle from '@mds/icons/CheckCircle';
import Info from '@mds/icons/Info';
import Warning from '@mds/icons/Warning';
import WarningCircle from '@mds/icons/WarningCircle';
import X from '@mds/icons/X';
import { ComponentsPropsList } from '@mui/material';
import { CheckboxCheckedIcon, CheckboxIcon, CheckboxIndeterminateIcon } from '../components/_internal/CheckboxIcon';
import DeleteIcon from '../components/_internal/DeleteIcon';
import { DialogPaper, DialogRoot } from '../components/_internal/Dialog';
import RadioButtonIcon from '../components/_internal/RadioButtonIcon';
import { closeIconClass } from './styleOverrides/alert';

export const muiTextFieldDefaultProps = {
  variant: 'filled',
  fullWidth: true,
  maxRows: 4,
} satisfies Partial<ComponentsPropsList['MuiTextField']>;

// TODO Move to styleOverrides. We had to keep default props separate due to MUI
// limitations, which no longer apply
export const defaultProps: any = {
  MuiAccordion: {
    disableGutters: true,
  },
  MuiAccordionSummary: {
    disableRipple: true,
    disableTouchRipple: true,
    expandIcon: <CaretDown />,
  },
  MuiAlert: {
    variant: 'soft',
    iconMapping: {
      success: <CheckCircle fontSize="lg" />,
      warning: <Warning fontSize="lg" />,
      error: <WarningCircle fontSize="lg" />,
      info: <Info fontSize="lg" />,
    },
    slots: {
      closeIcon: X,
    },
    slotProps: {
      closeIcon: { className: closeIconClass },
    },
  },
  MuiAutocomplete: {
    clearIcon: <X fontSize="small" />,
    popupIcon: <CaretDown fontSize="small" />,
  },
  MuiBreadcrumbs: {
    separator: '/',
  },
  MuiButton: {
    variant: 'contained',
    disableElevation: true,
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
  },
  MuiButtonBase: {
    disableRipple: true,
    disableTouchRipple: true,
  },
  MuiButtonGroup: {
    variant: 'contained',
    disableRipple: true,
    disableFocusRipple: true,
    disableElevation: true,
  },
  MuiCard: {
    elevation: 0,
  },
  MuiCardHeader: {
    slotProps: {
      title: { variant: 'subtitle1' },
      subheader: { variant: 'body1' },
    },
  },
  MuiChip: {
    deleteIcon: <DeleteIcon />,
    variant: 'outlined',
  },
  MuiCheckbox: {
    color: 'default',
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
    icon: <CheckboxIcon />,
    checkedIcon: <CheckboxCheckedIcon />,
    indeterminateIcon: <CheckboxIndeterminateIcon />,
  },
  MuiDialog: {
    fullWidth: true,
    maxWidth: 'sm',
    slots: { root: DialogRoot },
    PaperComponent: DialogPaper,
  },
  MuiDialogContentText: {
    variant: 'body1',
    color: 'textPrimary',
  },
  MuiFormControl: {
    variant: 'filled',
  },
  MuiIconButton: {
    color: 'default',
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
  },
  MuiInputBase: {
    // See CssBaseline
    // https://mui.com/material-ui/react-text-field/#performance
    disableInjectingGlobalStyles: true,
    maxRows: 4,
  },
  MuiLink: {
    color: 'primary',
    underline: 'hover',
  },
  MuiListItem: {
    divider: true,
  },
  MuiListItemButton: {
    divider: true,
    disableRipple: true,
    disableTouchRipple: true,
  },
  MuiListItemText: {
    slotProps: {
      primary: { variant: 'label2' },
      secondary: { variant: 'label3' },
    },
  },
  MuiMenuItem: {
    divider: false,
  },
  MuiPaginationItem: {
    slots: { next: CaretRightFill, previous: CaretLeftFill },
  },
  MuiPopover: {
    elevation: 0,
  },
  MuiRadio: {
    color: 'primary',
    icon: <RadioButtonIcon />,
    checkedIcon: <RadioButtonIcon checked />,
  },
  MuiSelect: {
    variant: 'filled',
    IconComponent: CaretDown,
  },
  MuiStack: {
    useFlexGap: true,
    spacing: 2,
  },
  MuiStepper: {
    orientation: 'horizontal',
    connector: null,
  },
  MuiSwitch: {
    color: 'primary',
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
  },
  MuiTab: {
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
  },
  MuiTablePagination: {
    slots: {
      actions: {
        previousButtonIcon: CaretLeftFill,
        nextButtonIcon: CaretRightFill,
        firstButtonIcon: CaretLineLeftFill,
        lastButtonIcon: CaretLineRightFill,
      },
    },
  },
  MuiTextField: muiTextFieldDefaultProps,
  MuiToggleButton: {
    disableRipple: true,
    disableFocusRipple: true,
    disableTouchRipple: true,
  },
  MuiToggleButtonGroup: {
    color: 'primary',
    variant: 'outlined',
  },
  MuiTooltip: {
    arrow: true,
    placement: 'top',
  },
} satisfies {
  [P in keyof ComponentsPropsList]?: Partial<ComponentsPropsList[P]>;
};

/**
 * Merge default component props into Theme.
 *
 * This needs to be called on Theme (result of createTheme()) rather than on ThemeOptions.
 * This is because createTheme() calls deepmerge(theme) internally, which would iterate
 * over default component props, including deeply nested React components and their object
 * properties. This might cause a max call stack error.
 *
 * TODO Remove this module - see comments for `defaultProps` above.
 */

export function addDefaultProps<T extends { components?: Record<string, any> }>(theme: T): T {
  if (!theme.components) {
    return theme;
  }

  const componentKeys = [...new Set([...Object.keys(theme.components), ...Object.keys(defaultProps)])];

  const components = Object.fromEntries(
    componentKeys.map((key) => {
      const originalEntry = theme.components![key];
      const newEntry = {
        ...originalEntry,
        defaultProps: {
          ...(defaultProps as Record<string, any>)[key],
          ...originalEntry?.defaultProps,
        },
      };
      return [key, newEntry];
    }),
  );

  return { ...theme, components };
}
